import { SearchIcon } from '@approvalmax/ui';
import { Button, Form, Grid, Popup, Text, TextField, toast } from '@approvalmax/ui/src/components';
import { useAtomValue } from 'jotai';
import { selectors } from 'modules/common';
import { useSelector } from 'modules/react-redux';
import { useCurrentUser } from 'modules/utils';
import { memo, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useDebounce } from 'react-use';
import { useChangeWatchers } from 'shared/data/webApp/v1';
import { ContentSplitViewMode, contentSplitViewState } from 'shared/states';

import { cancelActivePopup, reloadRequest } from '../../actions';
import { WatcherItem } from './components/WatcherItem/WatcherItem';
import { useGetRequest, useWatchersList } from './EditWatchersPopupContent.hooks';
import { messages } from './EditWatchersPopupContent.messages';
import { FormValues } from './EditWatchersPopupContent.types';

export const EditWatchersPopupContent = memo(() => {
    const dispatch = useDispatch();

    const request = useGetRequest();

    const team = useSelector((state) => (request ? selectors.company.getCompanyTeam(state, request.company) : []));
    const allUsers = useSelector(selectors.user.getUsers);
    const me = useCurrentUser();

    const contentSplitView = useAtomValue(contentSplitViewState);
    const isBillInSplitView =
        request &&
        contentSplitView.mode === ContentSplitViewMode.BillLinkedToBatchPayment &&
        contentSplitView.billId === request.id;

    const readOnly = !request || isBillInSplitView;

    const { mutate: changeWatchers, isLoading: isLoadingChangeWatchers } = useChangeWatchers({
        onSuccess: () => {
            toast.success(messages.successText);
            dispatch(cancelActivePopup());

            if (request) {
                dispatch(reloadRequest(request.id, request.companyId));
            }
        },
    });

    const requestWatchers = useMemo(() => {
        return (request?.watchers ?? []).reduce<string[]>((acc, requestWatcher) => {
            const watcherId = requestWatcher.watcher.databaseId;

            if (watcherId && allUsers.find((user) => user.databaseId === watcherId)) {
                acc.push(watcherId);
            }

            return acc;
        }, []);
    }, [request, allUsers]);

    const [search, setSearch] = useState('');
    const [searchValue, setSearchValue] = useState('');

    useDebounce(() => setSearch(searchValue), 300, [searchValue]);

    const defaultWatchers = useMemo(
        () => requestWatchers.reduce((watchers, watcherId) => ({ ...watchers, [watcherId]: true }), {}),
        [requestWatchers]
    );

    const form = useForm<FormValues>({
        defaultValues: {
            watchers: defaultWatchers,
        },
    });

    const onSubmit = form.handleSubmit(async (data) => {
        const { watchers } = data;

        const newWatcherIds = Object.keys(watchers).filter((id) => watchers[id]);

        const watchersToAdd = newWatcherIds.filter((newWatcherId) => !requestWatchers.includes(newWatcherId));
        const watchersToRemove = requestWatchers.filter((oldWatcherId) => !newWatcherIds.includes(oldWatcherId));

        if (request && (watchersToAdd.length || watchersToRemove.length)) {
            changeWatchers({
                body: {
                    requestId: request.id,
                    companyId: request.companyId,
                    watchersToAdd,
                    watchersToRemove,
                    isAddedDueToMention: false,
                },
            });
        } else {
            dispatch(cancelActivePopup());
        }
    });

    const { possibleWatchers, hasPossibleWatchers, noWatchersBySearch } = useWatchersList({
        request,
        me,
        team,
        search,
    });

    return (
        <Form form={form} onSubmit={onSubmit}>
            <Popup.Header
                title={readOnly ? messages.readOnlyPopupTitle : messages.popupTitle}
                actions={
                    <Button
                        disabled={!hasPossibleWatchers}
                        color='blue80'
                        size='medium'
                        type='submit'
                        progress={isLoadingChangeWatchers}
                    >
                        {messages.doneButtonText}
                    </Button>
                }
            />

            <Popup.Body>
                <Grid rowGap={8}>
                    {hasPossibleWatchers ? (
                        <TextField
                            placeholder={messages.searchPlaceholder}
                            size='small'
                            startIcon={<SearchIcon color='midnight60' size={12} />}
                            onChange={setSearchValue}
                        />
                    ) : (
                        <Text font='body' fontSize='medium' color='midnight70'>
                            {messages.noWatchers}
                        </Text>
                    )}

                    <div>
                        {possibleWatchers.map((user) => (
                            <WatcherItem key={user.databaseId} readonly={Boolean(readOnly)} user={user} />
                        ))}

                        {noWatchersBySearch && (
                            <Text font='body' fontSize='medium' color='midnight70'>
                                {messages.noWatchersBySearch}
                            </Text>
                        )}
                    </div>
                </Grid>
            </Popup.Body>
        </Form>
    );
});

EditWatchersPopupContent.displayName = 'EditWatchersPopupContent';
